import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { getConsent, setConsent, initClarity, revokeClarity } from "@/lib/clarity";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (consent === "accepted") initClarity();
    if (!consent) setVisible(true);

    const open = () => setVisible(true);
    window.addEventListener("open-cookie-preferences", open);
    return () => window.removeEventListener("open-cookie-preferences", open);
  }, []);

  const accept = () => {
    setConsent("accepted");
    initClarity();
    setVisible(false);
  };

  const decline = () => {
    setConsent("declined");
    revokeClarity();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-[9998] sm:max-w-md"
          data-testid="cookie-consent"
        >
          <div className="relative border border-white/15 bg-card/95 backdrop-blur-xl p-6 sm:p-7">
            <button
              onClick={decline}
              aria-label="Dismiss and decline"
              data-testid="cookie-close"
              className="absolute top-4 right-4 text-foreground/40 hover:text-primary transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <span className="grid place-items-center h-9 w-9 border border-primary/40 text-primary">
                <Cookie className="h-4 w-4" />
              </span>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
                Cookie Preferences
              </p>
            </div>

            <p className="text-sm text-foreground/70 leading-relaxed">
              We use privacy-friendly analytics (Microsoft Clarity) to understand how visitors use
              this site. No tracking runs until you accept. You can change this anytime.
            </p>

            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={accept}
                data-testid="cookie-accept"
                className="flex-1 bg-primary px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white hover:bg-white hover:text-background transition-colors duration-300"
              >
                Accept
              </button>
              <button
                onClick={decline}
                data-testid="cookie-decline"
                className="flex-1 border border-white/15 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/70 hover:border-primary hover:text-primary transition-colors duration-300"
              >
                Decline
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
